import { useState } from 'react';
import { cn } from '../../lib/utils.ts';

/** Past this many characters a value is clamped to two lines behind "Show more". */
export const LONG_VALUE = 200;

/** How a history line prints one value: empty is a dash, text as is, anything else as JSON. */
export const showValue = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

type Json = Record<string, unknown>;

/** A JSON object, or a string holding one (the history import's `legacyRaw`); else null. */
export function asObject(v: unknown): Json | null {
  if (typeof v === 'string') {
    if (!v.trimStart().startsWith('{')) return null;
    try {
      v = JSON.parse(v) as unknown;
      // eslint-disable-next-line crm/no-silent-catch -- probing JSON.parse; text that only starts with "{" is plain text
    } catch {
      return null;
    }
  }
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Json) : null;
}

/** JSON with object keys sorted, so a key order change is not a change. */
const stable = (v: unknown): string =>
  JSON.stringify(v, (_k, x: unknown) =>
    typeof x === 'object' && x !== null && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x as Json).sort(([a], [b]) => a.localeCompare(b)))
      : x,
  ) ?? 'undefined';

/** The top-level keys whose value differs between two objects, in name order. */
export function changedKeys(oldObj: Json, newObj: Json): string[] {
  const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  return [...keys]
    .filter((k) => stable(oldObj[k]) !== stable(newObj[k]))
    .sort((a, b) => a.localeCompare(b));
}

/** A value that may be long: over `LONG_VALUE` characters it is clamped, with "Show more". */
export function LongValue({ text, className }: { text: string; className?: string }) {
  const [open, setOpen] = useState(false);
  if (text.length <= LONG_VALUE) return <span className={className}>{text}</span>;
  return (
    <span className="inline-flex min-w-0 flex-col items-start">
      <span
        className={cn('[overflow-wrap:anywhere]', !open && 'line-clamp-2', className)}
        data-clamped={!open}
      >
        {text}
      </span>
      <button
        type="button"
        className="text-brand text-xs font-medium hover:underline"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Show less' : 'Show more'}
      </button>
    </span>
  );
}

/**
 * The "old → new" of one history line. Short values print as before. When both sides are JSON
 * objects (the history import's whole RecruitCRM record, `legacyRaw`), only the keys that changed
 * are listed, folded behind "Show N changed keys"; any other long value is clamped.
 */
export function ChangeValues({
  change,
  format = showValue,
}: {
  change: { old?: unknown; new?: unknown };
  /** How a whole value prints (the project history names stage and pipeline ids). */
  format?: (v: unknown) => string;
}) {
  const hasOld = 'old' in change;
  const hasNew = 'new' in change;
  const oldObj = hasOld ? asObject(change.old) : null;
  const newObj = hasNew ? asObject(change.new) : null;
  if (oldObj && newObj) return <ObjectChange oldObj={oldObj} newObj={newObj} />;
  return (
    <>
      {hasOld && (
        <LongValue text={format(change.old)} className="text-muted-foreground line-through" />
      )}
      {hasOld && hasNew && <span className="text-muted-foreground"> → </span>}
      {hasNew && <LongValue text={format(change.new)} />}
    </>
  );
}

function ObjectChange({ oldObj, newObj }: { oldObj: Json; newObj: Json }) {
  const [open, setOpen] = useState(false);
  const keys = changedKeys(oldObj, newObj);
  if (keys.length === 0) return <span className="text-muted-foreground">no change</span>;
  const label = `${keys.length} changed ${keys.length === 1 ? 'key' : 'keys'}`;
  return (
    <span className="flex basis-full flex-col items-start gap-1" data-testid="changed-keys">
      <button
        type="button"
        className="text-brand text-xs font-medium hover:underline"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? `Hide ${label}` : `Show ${label}`}
      </button>
      {open && (
        <ul className="border-border flex w-full flex-col gap-1 border-l-2 pl-3">
          {keys.map((k) => (
            <li key={k} className="flex min-w-0 flex-wrap gap-x-2" data-key={k}>
              <span className="font-medium">{k}:</span>
              {k in oldObj && (
                <LongValue
                  text={showValue(oldObj[k])}
                  className="text-muted-foreground line-through"
                />
              )}
              {k in oldObj && k in newObj && <span className="text-muted-foreground">→</span>}
              {k in newObj && <LongValue text={showValue(newObj[k])} />}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
